import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, User, Building, CreditCard, CheckCircle, XCircle, Clock, Loader, ChevronRight, HelpCircle, MessageCircle, FileText, AlertCircle, Plus } from 'lucide-react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
type VerificationItemStatus = 'pending' | 'under_review' | 'verified' | 'rejected';
interface PropertyVerification {
  address: string;
  documentType: string;
  status: VerificationItemStatus;
  feedback?: string;
}
interface VerificationData {
  overall: {
    status: VerificationItemStatus;
    progress: number;
    submittedAt: string;
  };
  identity: {
    status: VerificationItemStatus;
    documentType: string;
    documentNumber: string;
    uploadedAt: string;
    feedback?: string;
  };
  properties: PropertyVerification[];
  banking: {
    status: VerificationItemStatus;
    bankName: string;
    accountNumber: string;
    accountName: string;
    feedback?: string;
  };
  timeline: {
    step: string;
    title: string;
    date?: string;
    status: 'completed' | 'in_progress' | 'pending';
  }[];
}
// Mock verification data
const MOCK_VERIFICATION: VerificationData = {
  overall: {
    status: 'under_review',
    progress: 67,
    submittedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
  },
  identity: {
    status: 'verified',
    documentType: 'NIN',
    documentNumber: '***456789012',
    uploadedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
  },
  properties: [{
    address: '12 Admiralty Way, Lekki Phase 1, Lagos',
    documentType: 'Title Deed',
    status: 'verified'
  }, {
    address: '45 Gwarinpa Estate, Abuja',
    documentType: 'Certificate of Occupancy',
    status: 'pending'
  }],
  banking: {
    status: 'pending',
    bankName: 'GTBank',
    accountNumber: '***6789',
    accountName: 'Tunde Bakare'
  },
  timeline: [{
    step: 'submitted',
    title: 'Application Submitted',
    date: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    status: 'completed'
  }, {
    step: 'under_review',
    title: 'Under Review',
    date: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    status: 'completed'
  }, {
    step: 'identity',
    title: 'Identity Verified',
    date: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    status: 'completed'
  }, {
    step: 'ownership',
    title: 'Ownership Verification',
    status: 'in_progress'
  }, {
    step: 'banking',
    title: 'Bank Account Verification',
    status: 'pending'
  }, {
    step: 'approved',
    title: 'Final Approval',
    status: 'pending'
  }]
};
export function VerificationStatus() {
  const navigate = useNavigate();
  const verification = MOCK_VERIFICATION;
  const getStatusIcon = (status: VerificationItemStatus) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-5 w-5 text-green-500" data-id="element-4103" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" data-id="element-4104" />;
      case 'under_review':
        return <Loader className="h-5 w-5 text-blue-500 animate-spin" data-id="element-4105" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" data-id="element-4106" />;
    }
  };
  const getStatusBadge = (status: VerificationItemStatus) => {
    const styles = {
      verified: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      under_review: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    const labels = {
      verified: 'Verified',
      pending: 'Pending',
      under_review: 'Under Review',
      rejected: 'Rejected'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-bold ${styles[status]}`} data-id="element-4107">
        {labels[status]}
      </span>;
  };
  const getOverallStatusColor = () => {
    switch (verification.overall.status) {
      case 'verified':
        return 'text-green-500';
      case 'rejected':
        return 'text-red-500';
      default:
        return 'text-blue-500';
    }
  };
  const getProgressColor = () => {
    switch (verification.overall.status) {
      case 'verified':
        return '#22c55e';
      case 'rejected':
        return '#ef4444';
      default:
        return '#3b82f6';
    }
  };
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - verification.overall.progress / 100 * circumference;
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getTimelineStepIcon = (status: 'completed' | 'in_progress' | 'pending') => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-white" data-id="element-4108" />;
      case 'in_progress':
        return <Loader className="h-5 w-5 text-white animate-spin" data-id="element-4109" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-4110" />;
    }
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 lg:pb-0" data-id="element-4111">
      <Sidebar data-id="element-4112" />

      <main className="flex-1 lg:ml-[240px]" data-id="element-4113">
        <DashboardHeader data-id="element-4114" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4115">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-4116">
            <div className="flex items-center space-x-3 mb-4" data-id="element-4117">
              <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center" data-id="element-4118">
                <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-4119" />
              </div>
              <div data-id="element-4120">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4121">
                  Verification Status
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4122">
                  Track your verification progress
                </p>
              </div>
            </div>
          </motion.div>

          {/* Overall Progress Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6" data-id="element-4123">
            <div className="flex flex-col sm:flex-row items-center gap-6" data-id="element-4124">
              {/* Circular Progress */}
              <div className="relative" data-id="element-4125">
                <svg className="transform -rotate-90" width="120" height="120" data-id="element-4126">
                  <circle cx="60" cy="60" r="45" stroke="currentColor" strokeWidth="10" fill="none" className="text-gray-200 dark:text-gray-700" data-id="element-4127" />
                  <motion.circle cx="60" cy="60" r="45" stroke={getProgressColor()} strokeWidth="10" fill="none" strokeLinecap="round" initial={{
                  strokeDashoffset: circumference
                }} animate={{
                  strokeDashoffset
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut'
                }} style={{
                  strokeDasharray: circumference
                }} data-id="element-4128" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-4129">
                  <span className={`text-2xl font-bold ${getOverallStatusColor()}`} data-id="element-4130">
                    {verification.overall.progress}%
                  </span>
                </div>
              </div>

              {/* Status Info */}
              <div className="flex-1 text-center sm:text-left" data-id="element-4131">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2" data-id="element-4132">
                  {getStatusIcon(verification.overall.status)}
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4133">
                    {verification.overall.status === 'verified' ? 'Verification Complete!' : verification.overall.status === 'rejected' ? 'Verification Rejected' : 'Verification in Progress'}
                  </h2>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2" data-id="element-4134">
                  Submitted {formatDate(verification.overall.submittedAt)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-4135">
                  {verification.overall.status === 'verified' ? 'You can now list properties on Renta.' : 'Usually completed within 24 hours.'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Verification Sections */}
          <div className="space-y-4 mb-8" data-id="element-4136">
            {/* Identity Verification */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6" data-id="element-4137">
              <div className="flex items-start justify-between mb-4" data-id="element-4138">
                <div className="flex items-center space-x-3" data-id="element-4139">
                  <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center" data-id="element-4140">
                    <User className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-4141" />
                  </div>
                  <div data-id="element-4142">
                    <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-4143">
                      Identity Verification
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4144">
                      Government-issued ID verification
                    </p>
                  </div>
                </div>
                {getStatusBadge(verification.identity.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-4145">
                <div data-id="element-4146">
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-4147">
                    Document Type
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-4148">
                    {verification.identity.documentType}
                  </p>
                </div>
                <div data-id="element-4149">
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-4150">
                    Document Number
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-4151">
                    {verification.identity.documentNumber}
                  </p>
                </div>
                <div className="col-span-2" data-id="element-4152">
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-4153">Uploaded</p>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-4154">
                    {formatDate(verification.identity.uploadedAt)}
                  </p>
                </div>
              </div>

              {verification.identity.feedback && <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg" data-id="element-4155">
                  <div className="flex items-start space-x-2" data-id="element-4156">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-4157" />
                    <p className="text-sm text-red-700 dark:text-red-400" data-id="element-4158">
                      {verification.identity.feedback}
                    </p>
                  </div>
                </div>}
            </motion.div>

            {/* Property Ownership */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6" data-id="element-4159">
              <div className="flex items-start justify-between mb-4" data-id="element-4160">
                <div className="flex items-center space-x-3" data-id="element-4161">
                  <div className="h-10 w-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center" data-id="element-4162">
                    <Building className="h-5 w-5 text-green-600 dark:text-green-400" data-id="element-4163" />
                  </div>
                  <div data-id="element-4164">
                    <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-4165">
                      Property Ownership
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4166">
                      {verification.properties.length} properties submitted
                    </p>
                  </div>
                </div>
                {getStatusBadge(verification.properties.every(p => p.status === 'verified') ? 'verified' : verification.properties.some(p => p.status === 'rejected') ? 'rejected' : 'pending')}
              </div>

              <div className="space-y-3" data-id="element-4167">
                {verification.properties.map((property, index) => <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-4168">
                    <div className="flex items-start justify-between" data-id="element-4169">
                      <div className="flex-1" data-id="element-4170">
                        <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-4171">
                          {property.address}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-4172">
                          {property.documentType}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-4173">
                        {getStatusIcon(property.status)}
                      </div>
                    </div>
                    {property.feedback && <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-700 dark:text-red-400" data-id="element-4174">
                        {property.feedback}
                      </div>}
                  </div>)}
              </div>
            </motion.div>

            {/* Bank Account */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6" data-id="element-4175">
              <div className="flex items-start justify-between mb-4" data-id="element-4176">
                <div className="flex items-center space-x-3" data-id="element-4177">
                  <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center" data-id="element-4178">
                    <CreditCard className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-4179" />
                  </div>
                  <div data-id="element-4180">
                    <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-4181">
                      Bank Account Verification
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4182">
                      Payment account verification
                    </p>
                  </div>
                </div>
                {getStatusBadge(verification.banking.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-4183">
                <div data-id="element-4184">
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-4185">Bank</p>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-4186">
                    {verification.banking.bankName}
                  </p>
                </div>
                <div data-id="element-4187">
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-4188">
                    Account Number
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-4189">
                    {verification.banking.accountNumber}
                  </p>
                </div>
                <div className="col-span-2" data-id="element-4190">
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-4191">
                    Account Name
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-4192">
                    {verification.banking.accountName}
                  </p>
                </div>
              </div>

              {verification.banking.feedback && <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg" data-id="element-4193">
                  <div className="flex items-start space-x-2" data-id="element-4194">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-4195" />
                    <p className="text-sm text-red-700 dark:text-red-400" data-id="element-4196">
                      {verification.banking.feedback}
                    </p>
                  </div>
                </div>}
            </motion.div>
          </div>

          {/* Timeline */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6" data-id="element-4197">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-6" data-id="element-4198">
              Verification Timeline
            </h3>

            <div className="relative" data-id="element-4199">
              {verification.timeline.map((step, index) => <div key={step.step} className="flex items-start mb-6 last:mb-0" data-id="element-4200">
                  {/* Line */}
                  {index < verification.timeline.length - 1 && <div className={`absolute left-4 top-8 w-0.5 h-12 ${step.status === 'completed' ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} style={{
                transform: 'translateX(-50%)',
                top: `${index * 72 + 32}px`
              }} data-id="element-4201" />}

                  {/* Icon */}
                  <div className={`relative z-10 h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${step.status === 'completed' ? 'bg-green-500' : step.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-4202">
                    {getTimelineStepIcon(step.status)}
                  </div>

                  {/* Content */}
                  <div className="ml-4 flex-1" data-id="element-4203">
                    <p className={`font-medium ${step.status === 'pending' ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`} data-id="element-4204">
                      {step.title}
                    </p>
                    {step.date && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-4205">
                        {formatDate(step.date)}
                      </p>}
                    {step.status === 'in_progress' && <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5" data-id="element-4206">
                        In progress...
                      </p>}
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Next Steps Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className={`rounded-xl p-6 mb-6 ${verification.overall.status === 'verified' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : verification.overall.status === 'rejected' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'}`} data-id="element-4207">
            <h3 className={`font-semibold mb-2 ${verification.overall.status === 'verified' ? 'text-green-900 dark:text-green-100' : verification.overall.status === 'rejected' ? 'text-red-900 dark:text-red-100' : 'text-blue-900 dark:text-blue-100'}`} data-id="element-4208">
              {verification.overall.status === 'verified' ? "You're All Set!" : verification.overall.status === 'rejected' ? 'Action Required' : 'What Happens Next?'}
            </h3>
            <p className={`text-sm mb-4 ${verification.overall.status === 'verified' ? 'text-green-800 dark:text-green-200' : verification.overall.status === 'rejected' ? 'text-red-800 dark:text-red-200' : 'text-blue-800 dark:text-blue-200'}`} data-id="element-4209">
              {verification.overall.status === 'verified' ? 'Your verification is complete. You can now list properties and start receiving tenant applications.' : verification.overall.status === 'rejected' ? 'Your verification was rejected. Please review the feedback above and resubmit your documents.' : "Your verification is being reviewed by our team. We'll notify you via email and SMS once complete. This usually takes less than 24 hours."}
            </p>

            {verification.overall.status === 'verified' && <button onClick={() => navigate('/landlord/property/new')} className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors" data-id="element-4210">
                <Plus className="h-4 w-4 mr-2" data-id="element-4211" />
                List Your First Property
                <ChevronRight className="h-4 w-4 ml-1" data-id="element-4212" />
              </button>}

            {verification.overall.status === 'rejected' && <button onClick={() => navigate('/landlord/onboarding')} className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors" data-id="element-4213">
                Resubmit Documents
                <ChevronRight className="h-4 w-4 ml-1" data-id="element-4214" />
              </button>}
          </motion.div>

          {/* Support Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6" data-id="element-4215">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4" data-id="element-4216">
              Need Help?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-4217">
              <button className="flex items-center justify-center space-x-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-4218">
                <MessageCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-4219" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4220">
                  Live Chat
                </span>
              </button>
              <button onClick={() => navigate('/help')} className="flex items-center justify-center space-x-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-4221">
                <HelpCircle className="h-5 w-5 text-green-600 dark:text-green-400" data-id="element-4222" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4223">
                  FAQ
                </span>
              </button>
              <button className="flex items-center justify-center space-x-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-4224">
                <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-4225" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4226">
                  Contact Support
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}